import React, { useState, useContext } from "react";
import { TODAY } from "../../../utils/format";
import { locName, localName } from "../../../utils/helpers";
import { useValidationToast, validateFields } from "../../../utils/validation";
import GroceryLabel, { useGroceryPrefetch } from "../../../shared/GroceryLabel";
import { staffViewCan } from "../../../permissions/checks";
import authFetch from "../../../authFetch";
import PlatformCtx from "../../../state/PlatformCtx";

/* ══ STAFF GROCERIES ═══════════════════════════════════════
   These panels are intentionally bespoke rather than thin wrappers around
   the main-workspace HouseholdOps module. HouseholdOps is a 700+ line
   owner dashboard — grouped batch requests, task-engine syncing, in-line
   edit modals, analytics, AI panel, grouping by request-task — designed
   for a household manager monitoring everyone's groceries.

   The three staff flows are each small and different:
     – GroceryRunsPanel:    driver working through a pending queue
     – GroceryRequestPanel: maid submitting a batch list
     – GroceryHistoryPanel: maid reviewing their own past requests
   None of these maps cleanly to a subset of HouseholdOps — forcing reuse
   would require layering selfView / readOnly / bulkFormOnly flags that
   bloat the owner module without shortening the staff one. Keeping these
   panels purpose-built is the right trade-off. */

/* ── Fulfillment panel ─────────────────────────────────────
   Any staff role granted fulfillGroceryRuns can see and act on pending
   requests at their allowed locations. Mark as picked-up / done.
   Rendered with .card/.cr for main-workspace list parity. */
export function GroceryRunsPanel({state,dispatch,user,isRTL,lang}){
  const {locations}=state;
  const allowedLocs=user.allowedLocs||[];
  const filteredLocs=allowedLocs.length>0?locations.filter(l=>allowedLocs.includes(l.id)):locations;
  const pendingGroceries=(state.groceries||[]).filter(g=>filteredLocs.some(l=>l.id===g.locId)&&g.status!=="completed"&&g.status!=="Fulfilled");
  useGroceryPrefetch(pendingGroceries, lang);

  return(
    <div>
      <div className="notice" style={{marginBottom:14}}>
        {isRTL?"طلبات البقالة للمواقع المخصصة لك — حدّث الحالة عند الجلب.":"Pending grocery requests for your assigned locations. Update the status as you pick items up."}
      </div>

      <div className="card">
        <div className="ch"><div className="ct">{isRTL?"طلبات معلقة":"Pending requests"}</div><span style={{fontSize:11,color:"var(--g400)"}}>{pendingGroceries.length}</span></div>
        {!pendingGroceries.length&&<div className="empty">{isRTL?"لا طلبات بقالة معلقة":"No pending grocery runs."}</div>}
        {pendingGroceries.map(g=>{
          const loc=locations.find(l=>l.id===g.locId);
          const gs=g.status==="In Transit"?"in_progress":g.status==="Fulfilled"?"completed":g.status==="Pending"?"pending":(g.status||"pending");
          const GLBL={pending:"p-open",in_progress:"p-wait",completed:"p-done"};
          const GLBL2={pending:isRTL?"معلق":"Pending",in_progress:isRTL?"في الطريق":"In Transit",completed:isRTL?"مكتمل":"Fulfilled"};
          const nx=gs==="pending"?"in_progress":gs==="in_progress"?"completed":null;
          return(
            <div key={g.id} className="cr" style={{display:"flex",alignItems:"center",gap:10,padding:"12px 18px"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:14,fontWeight:500,marginBottom:2}}><GroceryLabel row={g} lang={lang}/></div>
                <div style={{fontSize:12,color:"var(--g400)"}}>{isRTL?"الكمية":"Qty"}: {g.qty}{g.unit?` ${g.unit}`:""} · {localName(loc, lang)}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                <span className={`pill ${GLBL[gs]||"p-open"}`}>{GLBL2[gs]||gs}</span>
                {nx&&<button className="btn btn-g btn-xs" onClick={()=>dispatch({type:"UPDATE_GROCERY",id:g.id,patch:{status:nx}})}>{GLBL2[nx]}</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Request panel ─────────────────────────────────────────
   Any staff role granted requestGroceries can batch-submit a list.
   The submission notifies whichever active staff member at the
   selected location holds fulfillGroceryRuns — capability-based, not
   role-based. Falls back to the workspace owner if no fulfiller. */
export function GroceryRequestPanel({state,dispatch,user,isRTL}){
  const platformCtx=useContext(PlatformCtx);
  const {locations,users}=state;
  const vt=useValidationToast();
  const allowedLocs=user.allowedLocs||[];
  const filteredLocs=allowedLocs.length>0?locations.filter(l=>allowedLocs.includes(l.id)):locations;
  const [selectedLoc,setSelectedLoc]=useState(user.assignedLoc||filteredLocs[0]?.id||"");
  const [batchItems,setBatchItems]=useState(Array.from({length:5},(_,i)=>({id:`bi${i}`,name:"",qty:"1"})));

  const updateBatch=(idx,field,val)=>setBatchItems(prev=>prev.map((it,i)=>i===idx?{...it,[field]:val}:it));
  const addRow=()=>{if(batchItems.length<20)setBatchItems(p=>[...p,{id:`bi${Date.now()}`,name:"",qty:"1"}]);};
  const removeRow=(idx)=>{if(batchItems.length>1)setBatchItems(p=>p.filter((_,i)=>i!==idx));};

  const submitBatch=async()=>{
    const valid=batchItems.filter(it=>it.name.trim());
    const m=validateFields([{ok:valid.length,label:isRTL?"عنصر واحد على الأقل":"At least 1 item"},{ok:selectedLoc,label:isRTL?"الموقع":"Location"}]);
    if(m.length){vt.show(m.join(", "));return;}
    /* Build grocery rows with canonical bilingual fields. Staff panel
       has no autocomplete so all rows are free-text; pin the typed
       string into the right slot by Arabic-character detection. */
    const isAr=s=>typeof s==="string"&&/[؀-ۿ]/.test(s);
    const items=valid.map(it=>{
      const typed=it.name.trim();
      const arVal=isAr(typed)?typed:"";
      const enVal=isAr(typed)?"":typed;
      return {id:`g${Date.now()}_${Math.random().toString(36).slice(2)}`,
        item:typed,itemEn:enVal,itemAr:arVal,itemSource:"user",
        qty:parseInt(it.qty)||1,unit:"",status:"pending",requestedBy:user.id,locId:selectedLoc,date:TODAY};
    });

    /* Notify the first active staff member permitted to fulfill grocery
       runs at this location — capability-based lookup, not role-literal. */
    const fulfiller=users.find(u=>
      u.active
      &&u.id!==user.id
      &&staffViewCan("fulfillGroceryRuns",u,state,platformCtx)
      &&(u.assignedLoc===selectedLoc||(u.allowedLocs||[]).includes(selectedLoc))
    );
    const ownerUser=users.find(u=>(platformCtx?.platformUserProfiles||[]).find(p=>p.id===u.role)?.id==="owner"&&u.active)||users[0];
    const locLabel=isRTL?locations.find(l=>l.id===selectedLoc)?.nameAr:locations.find(l=>l.id===selectedLoc)?.name;

    /* Dispatch grocery items + notifications locally for instant feedback.
       Both title/titleAr and body/bodyAr are populated so the recipient
       sees the notification in their own UI language (not the requester's
       language). NotifBell prefers the *Ar variants when lang==="ar". */
    items.forEach(item=>dispatch({type:"ADD_GROCERY",item}));
    const requesterEn=user.name||"";
    const requesterAr=user.nameAr||user.name||"";
    const locObjN=locations.find(l=>l.id===selectedLoc);
    const locEn=locObjN?.name||"";
    const locAr=locObjN?.nameAr||locEn;
    const time=new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
    const notif={id:`n${Date.now()}`,userId:fulfiller?.id||ownerUser?.id||"",type:"grocery",
      title:"New grocery run", titleAr:"طلب بقالة جديد",
      body:`${requesterEn} — ${items.length} item${items.length===1?"":"s"}${locEn?` · ${locEn}`:""}`,
      bodyAr:`${requesterAr} — ${items.length} ${items.length===1?"عنصر":"عناصر"}${locAr?` · ${locAr}`:""}`,
      link:"groceries",read:false,time,date:TODAY};
    const ownerNotif={id:`n${Date.now()+1}`,userId:ownerUser?.id||"",type:"grocery",
      title:"Grocery request", titleAr:"طلب بقالة",
      body:`${requesterEn} — ${items.length} item${items.length===1?"":"s"}`,
      bodyAr:`${requesterAr} — ${items.length} ${items.length===1?"عنصر":"عناصر"}`,
      link:"groceries",read:false,time,date:TODAY};
    dispatch({type:"ADD_NOTIF",notif});dispatch({type:"ADD_NOTIF",notif:ownerNotif});

    /* Create a linked task on the Task Engine so the fulfiller sees the
       run in their Tasks tab (not just as a notification). Mirrors the
       owner's HouseholdOps flow — same sourceModule, taskType, context
       payload, and linkedEntities shape, so downstream task↔grocery
       status sync works identically. Unless the workspace has opted out
       of auto-creating tasks (groceryConfig.autoCreateTask === false). */
    const grocConfig=state.groceryConfig||{};
    if(grocConfig.autoCreateTask!==false){
      try{
        const API=typeof window!=="undefined"?window.location.origin:"";
        const groceryIds=items.map(i=>i.id);
        const platGrocDefaults=(platformCtx?.platformGroceryConfig)||{};
        const priority=grocConfig.defaultPriority||platGrocDefaults.defaultPriority||"medium";
        /* Bilingual task title — see HouseholdOps for rationale. */
        const locObjT=locations.find(l=>l.id===selectedLoc);
        const locLabelT=locObjT?.name||"";
        const locLabelArT=locObjT?.nameAr||locLabelT;
        const taskTitle=`Grocery Run — ${items.length} item${items.length===1?"":"s"}${locLabelT?` — ${locLabelT}`:""}`;
        const taskTitleAr=`طلب بقالة — ${items.length} ${items.length===1?"عنصر":"عناصر"}${locLabelArT?` — ${locLabelArT}`:""}`;
        const res=await authFetch(`${API}/api/tasks?action=create_task`,{
          method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({ws_key:state.wsKey,actor_id:user.id,actor_name:user.name,
            task:{title:taskTitle, title_ar:taskTitleAr,
              description:"",priority,created_by:user.id,
              assigned_to:fulfiller?.id||null,
              assigned_to_name:fulfiller?.name||"",
              source_module:"Household",task_type:"grocery_run",loc_id:selectedLoc,
              context_payload:{groceryIds,itemCount:items.length,items:items.map(i=>({id:i.id,item:i.item,qty:i.qty,unit:i.unit}))},
              linked_entities:groceryIds.map(id=>({type:"grocery",id}))}})});
        const data=await res.json();
        if(data.ok&&data.task){
          groceryIds.forEach(gId=>dispatch({type:"UPDATE_GROCERY",id:gId,patch:{taskId:data.task.id}}));
          dispatch({type:"ADD_TASK",task:data.task});
        }
      }catch(e){console.error("[Staff grocery] task creation failed",e);}
    }

    setBatchItems(Array.from({length:5},(_,i)=>({id:`bi${i}`,name:"",qty:"1"})));
  };

  return(
    <div>
      {vt.Toast}
      {filteredLocs.length>1&&(
        <div style={{marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
          <div style={{fontSize:13,fontWeight:500,color:"var(--g600)",flexShrink:0}}>{isRTL?"الموقع:":"Location:"}</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {filteredLocs.map(l=><button key={l.id} className={`btn btn-sm ${selectedLoc===l.id?"btn-p":"btn-s"}`} onClick={()=>setSelectedLoc(l.id)}>{isRTL&&l.nameAr?l.nameAr:l.name}</button>)}
          </div>
        </div>
      )}

      <div className="card">
        <div className="ch">
          <div className="ct">{isRTL?"قائمة البقالة":"Grocery List"}</div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <span style={{fontFamily:"var(--fm)",fontSize:10,color:"var(--g400)"}}>{batchItems.filter(i=>i.name.trim()).length}/{batchItems.length} {isRTL?"عناصر":"items"}</span>
            {batchItems.length<20&&<button className="btn btn-s btn-xs" onClick={addRow}>+ {isRTL?"إضافة صف":"Add row"}</button>}
          </div>
        </div>
        <div style={{padding:"8px 0"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 72px 28px",gap:6,padding:"4px 16px 6px",borderBottom:"1px solid var(--g100)"}}>
            <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".07em"}}>{isRTL?"العنصر":"Item"}</div>
            <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".07em"}}>{isRTL?"الكمية":"Qty"}</div>
            <div/>
          </div>
          {batchItems.map((it,idx)=>(
            <div key={it.id} style={{display:"grid",gridTemplateColumns:"1fr 72px 28px",gap:6,padding:"4px 16px",alignItems:"center",background:it.name.trim()?"var(--gold-bg)":"transparent",transition:"background .08s"}}>
              <input className="fi" value={it.name} onChange={e=>updateBatch(idx,"name",e.target.value)} placeholder={`${isRTL?"عنصر":"Item"} ${idx+1}…`} style={{fontSize:13,padding:"6px 9px"}}
                onKeyDown={e=>{if(e.key==="Enter"&&idx===batchItems.length-1&&batchItems.length<20)addRow();}}/>
              <input className="fi" type="number" min="1" value={it.qty} onChange={e=>updateBatch(idx,"qty",e.target.value)} style={{fontSize:13,padding:"6px 9px",textAlign:"center"}}/>
              <button onClick={()=>removeRow(idx)} style={{width:24,height:24,borderRadius:"50%",border:"1px solid var(--g200)",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:"var(--g400)",transition:"all .1s",flexShrink:0}}
                onMouseEnter={e=>{e.currentTarget.style.background="var(--red-bg)";e.currentTarget.style.color="var(--red)";e.currentTarget.style.borderColor="var(--red-bd)";}}
                onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color="var(--g400)";e.currentTarget.style.borderColor="var(--g200)";}}>×</button>
            </div>
          ))}
        </div>
        <div style={{padding:"10px 16px",borderTop:"1px solid var(--g100)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
          <div style={{fontSize:12,color:"var(--g500)"}}>
            {batchItems.filter(i=>i.name.trim()).length>0
              ?isRTL?`${batchItems.filter(i=>i.name.trim()).length} عناصر جاهزة للإرسال`:`${batchItems.filter(i=>i.name.trim()).length} items ready to submit`
              :isRTL?"أضف عناصر أعلاه":"Add items above"}
          </div>
          <button className="btn btn-p" onClick={submitBatch} disabled={!batchItems.filter(i=>i.name.trim()).length||!selectedLoc} style={{opacity:!batchItems.filter(i=>i.name.trim()).length||!selectedLoc?0.45:1}}>
            {isRTL?"إرسال الطلب":"Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── History panel ─────────────────────────────────────────
   Past requests this staff user submitted. Any staff role granted
   viewOwnGroceryHistory can see this tab. */
export function GroceryHistoryPanel({state,user,isRTL,lang}){
  const {groceries,locations}=state;
  const mine=groceries.filter(g=>g.requestedBy===user.id);
  useGroceryPrefetch(mine, lang);
  return(
    <div className="card">
      <div className="ch"><div className="ct">{isRTL?"الطلبات السابقة":"Past Requests"}</div><span style={{fontSize:11,color:"var(--g400)"}}>{mine.length}</span></div>
      {mine.length===0&&<div className="empty">{isRTL?"لا طلبات بعد":"No requests yet."}</div>}
      {mine.map(g=>{
        const gs=g.status==="In Transit"?"in_progress":g.status==="Fulfilled"?"completed":g.status==="Pending"?"pending":(g.status||"pending");
        const GPL={pending:"p-open",in_progress:"p-wait",completed:"p-done"};
        const GL2={pending:isRTL?"معلق":"Pending",in_progress:isRTL?"في الطريق":"In Transit",completed:isRTL?"مكتمل":"Fulfilled"};
        return(
          <div key={g.id} className="cr" style={{display:"flex",alignItems:"center",gap:10,padding:"12px 18px"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:14,fontWeight:500,letterSpacing:"-.01em"}}><GroceryLabel row={g} lang={lang}/></div>
              <div style={{fontSize:12,color:"var(--g400)"}}>
                {isRTL?"الكمية":"Qty"}: {g.qty} · {g.date} · {localName(locations.find(l=>l.id===g.locId), lang) || locName(locations,g.locId)}
              </div>
            </div>
            <span className={`pill ${GPL[gs]||"p-open"}`} style={{flexShrink:0}}>{GL2[gs]||gs}</span>
          </div>
        );
      })}
    </div>
  );
}
